import API_ENDPOINTS from "@/common/api/apiEndpoints";
import httpClient from "@/common/api/httpClient";
import httpMethods from "@/common/api/httpMethods";
import type {
  Cycle,
  CycleDetail,
  CycleSummary,
  CreateCyclePayload,
  UpdateCyclePayload,
  CloseCyclePayload,
  ReopenCyclePayload,
  CloneCyclePayload,
  CycleRun,
  TriggerRunPayload,
  CycleRunDetail,
  CycleCaseResult,
  CycleStepResult,
  ScopeChangeEvent,
  CoverageMatrix,
  Paginated,
  CycleReportSummary,
  ReportByTesterRow,
  ReportByConfigRow,
  RiskItem,
  CycleDashboard,
  CycleInbox,
  SoloContext,
  CycleDefect,
  DashboardInsight,
  AskTheDataAnswer,
  CycleAiSummary,
  ReportShareToken,
  ReportAccessLevel,
  ReportScopeType,
  ReportEmailSchedule,
  SharedReport,
} from "@/common/types/Cycle";

interface QueryParams {
  workspaceVersionId?: number;
  releaseFolderId?: number;
  projectId?: number;
  status?: string;
  type?: string;
  page?: number;
  size?: number;
  query?: string;
  sort?: string;
}

const buildQueryParams = (params: QueryParams): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null)
      searchParams.set(key, String(value));
  });
  return `?${searchParams.toString()}`;
};

// ΓöÇΓöÇΓöÇ Cycle CRUD ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

export const fetchCycles = (params: QueryParams) =>
  httpClient<{ data: Paginated<CycleSummary> }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.cycles}${buildQueryParams(params)}`,
  ).then((res) => res.data.data);

/** Test Manager org-wide cycle list ΓÇö returns all cycles across every project in the org. */
export const fetchCyclesOrgWide = (
  params: Omit<QueryParams, "workspaceVersionId"> = {},
) =>
  httpClient<{ data: Paginated<CycleSummary> }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.cycles}${buildQueryParams({ ...params, orgWide: true } as any)}`,
  ).then((res) => res.data.data);

export const fetchCycleById = (cycleId: number) =>
  httpClient<{ data: CycleDetail }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}`,
  ).then((res) => res.data.data);

export const createCycle = (payload: CreateCyclePayload) =>
  httpClient<{ data: Cycle }>(
    httpMethods.POST,
    API_ENDPOINTS.CYCLES.cycles,
    payload,
  ).then((res) => res.data.data);

export const updateCycle = (cycleId: number, payload: UpdateCyclePayload) =>
  httpClient<{ data: Cycle }>(
    httpMethods.PUT,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}`,
    payload,
  ).then((res) => res.data.data);

export const deleteCycle = (cycleId: number) =>
  httpClient<void>(
    httpMethods.DELETE,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}`,
  );

// ΓöÇΓöÇΓöÇ Lifecycle ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

export const activateCycle = (cycleId: number) =>
  httpClient<{ data: Cycle }>(
    httpMethods.POST,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/activate`,
  ).then((res) => res.data.data);

export const closeCycle = (cycleId: number, payload?: CloseCyclePayload) =>
  httpClient<{ data: Cycle }>(
    httpMethods.POST,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/close`,
    payload,
  ).then((res) => res.data.data);

export const reopenCycle = (cycleId: number, payload: ReopenCyclePayload) =>
  httpClient<{ data: Cycle }>(
    httpMethods.POST,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/reopen`,
    payload,
  ).then((res) => res.data.data);

export const cloneCycle = (cycleId: number, payload: CloneCyclePayload) =>
  httpClient<{ data: Cycle }>(
    httpMethods.POST,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/clone`,
    payload,
  ).then((res) => res.data.data);

// ΓöÇΓöÇΓöÇ Scope History ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

export const fetchScopeHistory = (cycleId: number) =>
  httpClient<{ data: ScopeChangeEvent[] }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/scope-history`,
  ).then((res) => res.data.data ?? []);

// ΓöÇΓöÇΓöÇ Reports ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

export const fetchCoverage = (cycleId: number) =>
  httpClient<{ data: CoverageMatrix }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/report/coverage`,
  ).then((res) => res.data.data);

export interface RequirementCoverage {
  requirements: {
    requirement_id: number | null;
    requirement_name: string;
    coverage: "covered" | "at_risk" | "partial";
    total_cases: number;
    passed: number;
    failed: number;
    blocked: number;
    cases: { status: string; case_id: number; case_name: string }[];
  }[];
  total: number;
  covered: number;
  coverage_pct: number;
}

/** BR-24/25: requirement coverage report for a cycle. */
export const fetchRequirementCoverage = (cycleId: number) =>
  httpClient<{ data: RequirementCoverage }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/report/requirement-coverage`,
  ).then((res) => res.data.data);

// ΓöÇΓöÇΓöÇ Runs (automated cycles) ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

export const fetchCycleRuns = (cycleId: number, params?: { limit?: number }) =>
  httpClient<{ data: CycleRun[] }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/runs${
      params?.limit ? `?limit=${params.limit}` : ""
    }`,
  ).then((res) => res.data.data);

export const triggerRun = (cycleId: number, payload: TriggerRunPayload) =>
  httpClient<{ data: CycleRun }>(
    httpMethods.POST,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/runs`,
    {
      run_filter: payload.filter,
      ...(payload.filter === "custom"
        ? { custom_case_ids: payload.test_case_ids ?? [] }
        : {}),
    },
  ).then((res) => res.data.data);

export const stopRun = (cycleId: number, runId: number) =>
  httpClient<{ data: CycleRun }>(
    httpMethods.POST,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/runs/${runId}/stop`,
  ).then((res) => res.data.data);

export const fetchRunDetail = (cycleId: number, runId: number) =>
  httpClient<{ data: CycleRunDetail }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/runs/${runId}`,
  ).then((res) => res.data.data);

export const fetchRunConfigCases = (
  cycleId: number,
  runId: number,
  configResultId: number,
) =>
  httpClient<{ data: CycleCaseResult[] }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/runs/${runId}/configs/${configResultId}/cases`,
  ).then((res) => res.data.data);

export const fetchRunCaseIterations = (
  cycleId: number,
  runId: number,
  caseResultId: number,
) =>
  httpClient<{ data: CycleCaseResult[] }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/runs/${runId}/cases/${caseResultId}/iterations`,
  ).then((res) => res.data.data);

export const fetchRunCaseSteps = (
  cycleId: number,
  runId: number,
  caseResultId: number,
) =>
  httpClient<{ data: CycleStepResult[] }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/runs/${runId}/cases/${caseResultId}/steps`,
  ).then((res) => res.data.data);

// ΓöÇΓöÇΓöÇ Scope edit (remove cases) ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

export const removeCasesFromCycle = (cycleId: number, cycleCaseIds: number[]) =>
  httpClient<{ data: { removed: number } }>(
    httpMethods.DELETE,
    `${API_ENDPOINTS.CYCLES.cycles}/${cycleId}/cases`,
    { cycle_case_ids: cycleCaseIds },
  ).then((res) => res.data.data);

// ΓöÇΓöÇΓöÇ Reports (server-computed) ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

export const fetchReportSummary = (cycleId: number, window?: string) =>
  httpClient<{ data: CycleReportSummary }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.reportSummary(cycleId)}${window ? `?window=${window}` : ""}`,
  ).then((res) => res.data.data);

export const fetchReportByTester = (cycleId: number) =>
  httpClient<{ data: ReportByTesterRow[] }>(
    httpMethods.GET,
    API_ENDPOINTS.CYCLES.reportByTester(cycleId),
  ).then((res) => res.data.data ?? []);

export interface DashboardFilterParams {
  cycleIds?: number[];
  types?: string[];
  statuses?: string[];
  priorities?: string[];
  assignees?: number[];
  configIds?: number[];
  moduleIds?: number[];
}

export interface DashboardParams {
  workspaceVersionId?: number;
  releaseFolderId?: number;
  projectId?: number;
  /** Direct cycle scope */
  cycleId?: number;
  /** Narrow a project-scoped dashboard to a single workspace within the project. */
  workspaceId?: number;
  window?: string;
  /** Custom date-range start (ISO yyyy-mm-dd) ΓÇö bounds date-sensitive metrics. */
  fromDate?: string;
  /** Custom date-range end (ISO yyyy-mm-dd). */
  toDate?: string;
  includeSubfolders?: boolean;
  orgWide?: boolean;
  filters?: DashboardFilterParams;
}

/** Cross-cycle PM Dashboard rollup (Release-level or whole workspace). */
export const fetchDashboard = (params: DashboardParams) => {
  const qs = new URLSearchParams();
  if (params.orgWide) {
    qs.set("orgWide", "true");
  } else if (params.workspaceVersionId != null) {
    qs.set("workspaceVersionId", String(params.workspaceVersionId));
  }
  if (params.releaseFolderId != null)
    qs.set("release_folder_id", String(params.releaseFolderId));
  if (params.projectId != null) qs.set("projectId", String(params.projectId));
  if (params.workspaceId != null)
    qs.set("workspaceId", String(params.workspaceId));
  if (params.window) qs.set("window", params.window);
  if (params.fromDate) qs.set("from", params.fromDate);
  if (params.toDate) qs.set("to", params.toDate);
  if (params.includeSubfolders) qs.set("includeSubfolders", "true");
  const f = params.filters;
  if (f) {
    const csv = (arr?: (number | string)[]) =>
      arr && arr.length ? arr.join(",") : undefined;
    const cycleIds = csv(f.cycleIds);
    if (cycleIds) qs.set("cycleIds", cycleIds);
    const types = csv(f.types);
    if (types) qs.set("types", types);
    const statuses = csv(f.statuses);
    if (statuses) qs.set("statuses", statuses);
    const priorities = csv(f.priorities);
    if (priorities) qs.set("priorities", priorities);
    const assignees = csv(f.assignees);
    if (assignees) qs.set("assignees", assignees);
    const configIds = csv(f.configIds);
    if (configIds) qs.set("configIds", configIds);
    const moduleIds = csv(f.moduleIds);
    if (moduleIds) qs.set("moduleIds", moduleIds);
  }
  const query = qs.toString();
  return httpClient<{ data: CycleDashboard }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.dashboard}${query ? `?${query}` : ""}`,
  ).then((res) => res.data.data);
};

/** Records-capable dashboard totals ΓÇö the 4 top cards drill down to real records. */
export type DashboardRecordMetric =
  | "test_cases"
  | "requirements"
  | "defects"
  | "cycles";

export interface DashboardRecord {
  id: number;
  title: string;
  subtitle?: string;
  project_id: number;
  project_name: string;
  /** The record's real workspace + version, so a deep-link can switch the active
   * scope to exactly where the record lives (not the project's first workspace). */
  workspace_id: number;
  workspace_version_id: number;
  workspace_name: string;
}

export interface DashboardRecordsResult {
  records: DashboardRecord[];
  total: number;
  truncated: boolean;
}

/**
 * Drill-down: list the actual records behind a dashboard total (e.g. the 14
 * defects), each tagged with its project. Uses the same scope as fetchDashboard so
 * the records match the count shown on the card.
 */
export const fetchDashboardRecords = (
  metric: DashboardRecordMetric,
  params: Pick<
    DashboardParams,
    "workspaceVersionId" | "projectId" | "workspaceId" | "orgWide"
  > & {
    defectStatus?: string;
  },
) => {
  const qs = new URLSearchParams();
  qs.set("metric", metric);
  if (params.orgWide) {
    qs.set("orgWide", "true");
  } else if (params.workspaceVersionId != null) {
    qs.set("workspaceVersionId", String(params.workspaceVersionId));
  }
  if (params.projectId != null) qs.set("projectId", String(params.projectId));
  if (params.workspaceId != null)
    qs.set("workspaceId", String(params.workspaceId));
  if (params.defectStatus) qs.set("defectStatus", params.defectStatus);
  return httpClient<{ data: DashboardRecordsResult }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.dashboard}/records?${qs.toString()}`,
  ).then((res) => res.data.data);
};

export interface ScopeCutCase {
  cycle_case_id: number;
  cycle_id: number;
  cycle_name: string;
  test_case_id: number;
  test_case_name: string;
  priority: string;
  status: string;
  weighted_remaining: number;
}

export interface ScopeCutResult {
  cases: ScopeCutCase[];
  cut_weighted: number;
}

export const fetchScopeCut = (params: {
  workspaceVersionId?: number;
  releaseFolderId?: number;
  n: number;
  pinnedIds?: number[];
}) => {
  const qs = new URLSearchParams();
  if (params.workspaceVersionId != null)
    qs.set("workspaceVersionId", String(params.workspaceVersionId));
  if (params.releaseFolderId != null)
    qs.set("release_folder_id", String(params.releaseFolderId));
  qs.set("n", String(params.n));
  if (params.pinnedIds?.length)
    qs.set("pinned_ids", params.pinnedIds.join(","));
  return httpClient<{ data: ScopeCutResult }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.scopeCut}?${qs.toString()}`,
  ).then((res) => res.data.data);
};

export const fetchReportByConfig = (cycleId: number) =>
  httpClient<{ data: ReportByConfigRow[] }>(
    httpMethods.GET,
    API_ENDPOINTS.CYCLES.reportByConfig(cycleId),
  ).then((res) => res.data.data ?? []);

export const fetchReportRisk = (cycleId: number) =>
  httpClient<{ data: RiskItem[] }>(
    httpMethods.GET,
    API_ENDPOINTS.CYCLES.reportRisk(cycleId),
  ).then((res) => res.data.data ?? []);

export const captureReportSnapshot = (cycleId: number) =>
  httpClient<{ data: unknown }>(
    httpMethods.POST,
    API_ENDPOINTS.CYCLES.reportSnapshot(cycleId),
  ).then((res) => res.data.data);

/** Returns the raw CSV string for client-side download. */
export const exportReportCsv = (cycleId: number) =>
  httpClient<string>(
    httpMethods.GET,
    API_ENDPOINTS.CYCLES.reportExport(cycleId),
  ).then((res) => res.data);

// ΓöÇΓöÇΓöÇ AI Dashboard endpoints (┬º4.10 / ┬º4.12 / ┬º6.5) ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

export const fetchDashboardInsights = (params: DashboardParams) => {
  const qs = new URLSearchParams();
  if (params.workspaceVersionId != null)
    qs.set("workspaceVersionId", String(params.workspaceVersionId));
  if (params.releaseFolderId != null)
    qs.set("release_folder_id", String(params.releaseFolderId));
  if (params.window) qs.set("window", params.window);
  return httpClient<{ data: DashboardInsight[] }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.dashboardInsights}${qs.toString() ? `?${qs.toString()}` : ""}`,
  ).then((res) => res.data.data ?? []);
};

export const askDashboard = (question: string, params: DashboardParams) => {
  const qs = new URLSearchParams();
  if (params.workspaceVersionId != null)
    qs.set("workspaceVersionId", String(params.workspaceVersionId));
  if (params.releaseFolderId != null)
    qs.set("release_folder_id", String(params.releaseFolderId));
  if (params.window) qs.set("window", params.window);
  return httpClient<{ data: AskTheDataAnswer }>(
    httpMethods.POST,
    `${API_ENDPOINTS.CYCLES.dashboardAsk}${qs.toString() ? `?${qs.toString()}` : ""}`,
    { question },
  ).then((res) => res.data.data);
};

export const fetchCycleAiSummary = (cycleId: number) =>
  httpClient<{ data: CycleAiSummary }>(
    httpMethods.POST,
    API_ENDPOINTS.CYCLES.reportAiSummary(cycleId),
  ).then((res) => res.data.data);

// ΓöÇΓöÇΓöÇ Share links (┬º6.6) ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

export interface CreateShareTokenPayload {
  ttl_days?: number | null;
  access_level?: ReportAccessLevel;
  password?: string | null;
  download_allowed?: boolean;
  report_type?: string;
  report_title?: string | null;
  report_scope_type?: ReportScopeType;
  report_scope_id?: number | null;
  workspace_version_id?: number | null;
  release_folder_id?: number | null;
  report_window?: string | null;
  workspace_name?: string | null;
  project_name?: string | null;
  generated_by_name?: string | null;
  summary_notes?: string | null;
  /** Column visibility map snapshotted into the shared report payload. */
  column_visibility?: Record<string, boolean> | null;
  /** For allure reports: the test-run id whose HTML report the link serves. */
  allure_run_id?: number | null;
  /** Active dashboard filter dimensions snapshotted at share-creation time. */
  dashboard_filters?: DashboardFilterParams | null;
}

export const createReportShareToken = (
  reportScopeId: number,
  payload: CreateShareTokenPayload | number = 30,
) =>
  httpClient<{ data: ReportShareToken }>(
    httpMethods.POST,
    API_ENDPOINTS.CYCLES.reportShare(reportScopeId),
    typeof payload === "number" ? { ttl_days: payload } : payload,
  ).then((res) => res.data.data);

export const fetchReportShareTokens = (
  reportScopeId: number,
  reportType?: string,
) =>
  httpClient<{ data: ReportShareToken[] }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.reportShare(reportScopeId)}${reportType ? `?report_type=${encodeURIComponent(reportType)}` : ""}`,
  ).then((res) => res.data.data ?? []);

export const updateReportShareToken = (
  cycleId: number,
  tokenId: number,
  patch: Partial<CreateShareTokenPayload & { is_revoked?: boolean }>,
) =>
  httpClient<{ data: { ok: boolean } }>(
    httpMethods.PATCH,
    API_ENDPOINTS.CYCLES.reportShareToken(cycleId, tokenId),
    patch,
  ).then((res) => res.data.data);

export const revokeReportShareToken = (cycleId: number, tokenId: number) =>
  httpClient<{ data: { ok: boolean } }>(
    httpMethods.POST,
    API_ENDPOINTS.CYCLES.reportShareRevoke(cycleId, tokenId),
  ).then((res) => res.data.data);

export const deleteReportShareToken = (cycleId: number, tokenId: number) =>
  httpClient<{ data: unknown }>(
    httpMethods.DELETE,
    API_ENDPOINTS.CYCLES.reportShareDelete(cycleId, tokenId),
  ).then((res) => res.data.data);

export const fetchPublicReport = (token: string) =>
  httpClient<{
    data: SharedReport | { requires_password: true; token: string };
  }>(httpMethods.GET, API_ENDPOINTS.CYCLES.publicReport(token), undefined, {
    skipAuthRedirect: true,
    timeout: 60_000,
  }).then((res) => res.data.data);

export const fetchPublicExecution = (token: string, cycleCaseId: number) =>
  // Import ApiExecution dynamically or assume it returns an ApiExecution
  httpClient<{ data: any }>(
    httpMethods.GET,
    API_ENDPOINTS.CYCLES.publicReportExecution(token, cycleCaseId),
    undefined,
    { skipAuthRedirect: true, timeout: 60_000 },
  ).then((res) => res.data.data);

export const fetchSavedReportDrafts = (scopeId?: number, reportType?: string) =>
  httpClient<{ data: any[] }>(
    httpMethods.GET,
    `reports/custom-drafts${scopeId || reportType ? `?${scopeId ? `scope_id=${scopeId}&` : ""}${reportType ? `report_type=${encodeURIComponent(reportType)}` : ""}` : ""}`,
  ).then((res) => res.data.data ?? []);

export const fetchSavedReportDraftById = (id: number) =>
  httpClient<{ data: any }>(
    httpMethods.GET,
    `reports/custom-drafts/${id}`,
  ).then((res) => res.data.data);

export const createSavedReportDraft = (payload: any) =>
  httpClient<{ data: any }>(
    httpMethods.POST,
    "reports/custom-drafts",
    payload,
  ).then((res) => res.data.data);

export const updateSavedReportDraft = (id: number, payload: any) =>
  httpClient<{ data: any }>(
    httpMethods.PUT,
    `reports/custom-drafts/${id}`,
    payload,
  ).then((res) => res.data.data);

export const deleteSavedReportDraft = (id: number) =>
  httpClient<{ success: boolean }>(
    httpMethods.DELETE,
    `reports/custom-drafts/${id}`,
  ).then((res) => res.data);

export const verifyPublicReportPassword = (token: string, password: string) =>
  httpClient<{ data: SharedReport }>(
    httpMethods.POST,
    API_ENDPOINTS.CYCLES.publicReportVerify(token),
    { password },
    { timeout: 60_000 },
  ).then((res) => res.data.data);

// ΓöÇΓöÇΓöÇ Email schedules (┬º6.4) ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

export const fetchReportEmailSchedules = (cycleId: number) =>
  httpClient<{ data: ReportEmailSchedule[] }>(
    httpMethods.GET,
    API_ENDPOINTS.CYCLES.reportEmailSchedules(cycleId),
  ).then((res) => res.data.data ?? []);

export const createReportEmailSchedule = (
  cycleId: number,
  payload: Omit<
    ReportEmailSchedule,
    "id" | "cycle_id" | "last_sent_at" | "next_send_at" | "created_at"
  >,
) =>
  httpClient<{ data: ReportEmailSchedule }>(
    httpMethods.POST,
    API_ENDPOINTS.CYCLES.reportEmailSchedules(cycleId),
    payload,
  ).then((res) => res.data.data);

export const updateReportEmailSchedule = (
  cycleId: number,
  scheduleId: number,
  payload: Partial<Omit<ReportEmailSchedule, "id" | "cycle_id" | "created_at">>,
) =>
  httpClient<{ data: ReportEmailSchedule }>(
    httpMethods.PUT,
    API_ENDPOINTS.CYCLES.reportEmailSchedule(cycleId, scheduleId),
    payload,
  ).then((res) => res.data.data);

export const deleteReportEmailSchedule = (
  cycleId: number,
  scheduleId: number,
) =>
  httpClient<{ data: unknown }>(
    httpMethods.DELETE,
    API_ENDPOINTS.CYCLES.reportEmailSchedule(cycleId, scheduleId),
  ).then((res) => res.data.data);

// ΓöÇΓöÇΓöÇ My Queue ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
// NOTE: My Queue lives in the cycle-cases service (fetchMyQueue/useMyQueue).

// ΓöÇΓöÇΓöÇ Inbox ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

export const fetchInbox = (params?: {
  workspaceVersionId?: number | null;
  projectId?: number | null;
}) => {
  const sp = new URLSearchParams();
  if (params?.workspaceVersionId != null)
    sp.set("workspace_version_id", String(params.workspaceVersionId));
  if (params?.projectId != null) sp.set("project_id", String(params.projectId));
  const qs = sp.toString() ? `?${sp.toString()}` : "";
  return httpClient<{ data: CycleInbox }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.inbox}${qs}`,
  ).then((res) => res.data.data);
};

export const archiveInbox = (cycleId: number, projectId?: number | null) =>
  httpClient<{ data: unknown }>(
    httpMethods.POST,
    API_ENDPOINTS.CYCLES.inboxArchive,
    {
      cycle_id: cycleId,
      project_id: projectId ?? null,
    },
  ).then((res) => res.data.data);

// ΓöÇΓöÇΓöÇ Solo context / invite banner ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

export const fetchSoloContext = (projectId: number) =>
  httpClient<{ data: SoloContext }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.soloContext}?project_id=${encodeURIComponent(projectId)}`,
  ).then((res) => res.data.data);

export const dismissInviteBanner = (projectId: number) =>
  httpClient<{ data: unknown }>(
    httpMethods.POST,
    API_ENDPOINTS.CYCLES.dismissBanner,
    {
      project_id: projectId,
    },
  ).then((res) => res.data.data);

// ΓöÇΓöÇΓöÇ Cycle defects ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

export const fetchCycleDefects = (cycleId: number) =>
  httpClient<{ data: CycleDefect[] }>(
    httpMethods.GET,
    API_ENDPOINTS.CYCLES.defects(cycleId),
  ).then((res) => res.data.data ?? []);

export const fetchOrphanedDefects = (cycleId: number) =>
  httpClient<{ data: CycleDefect[] }>(
    httpMethods.GET,
    API_ENDPOINTS.CYCLES.orphanedDefects(cycleId),
  ).then((res) => res.data.data ?? []);

export const transitionCycleDefect = (defectId: number, status: string) =>
  httpClient<{ data: unknown }>(
    httpMethods.POST,
    API_ENDPOINTS.CYCLES.defectTransition(defectId),
    {
      status,
    },
  ).then((res) => res.data.data);

export const rebindCycleDefect = (defectId: number, cycleId: number) =>
  httpClient<{ data: unknown }>(
    httpMethods.POST,
    API_ENDPOINTS.CYCLES.defectRebind(defectId),
    {
      cycle_id: cycleId,
    },
  ).then((res) => res.data.data);

export interface ReferencingCyclesResult {
  open: {
    id: number;
    name: string;
    status: string;
    start_date: string | null;
    end_date: string | null;
    case_count: number;
  }[];
  closed_count: number;
}

/** BR-50: open cycles referencing a test case + closed count (delete warning). */
export const fetchCyclesReferencingCase = (testCaseId: number) =>
  httpClient<{ data: ReferencingCyclesResult }>(
    httpMethods.GET,
    API_ENDPOINTS.CYCLES.referencingCase(testCaseId),
  ).then((res) => res.data.data);

export interface VersionUpdateCandidates {
  latest_version_number: number | null;
  cycles: { id: number; name: string; pinned_version: number | null }[];
}

/** BR-66: open cycles pinning an older version of a test case (update candidates). */
export const fetchVersionUpdateCandidates = (testCaseId: number) =>
  httpClient<{ data: VersionUpdateCandidates }>(
    httpMethods.GET,
    API_ENDPOINTS.CYCLES.caseVersionCandidates(testCaseId),
  ).then((res) => res.data.data);

/** BR-66: push the latest version of a test case to selected open cycles. */
export const pushVersionToCycles = (testCaseId: number, cycleIds: number[]) =>
  httpClient<{ data: { updated: number } }>(
    httpMethods.POST,
    API_ENDPOINTS.CYCLES.pushVersionToCycles(testCaseId),
    { cycle_ids: cycleIds },
  ).then((res) => res.data.data);

export const fetchExecutionTrend = (
  params: DashboardParams,
  mode: "automation" | "manual" | "all" = "all",
) => {
  const qs = new URLSearchParams();
  if (params.cycleId != null) qs.set("cycleId", String(params.cycleId));
  if (params.orgWide) qs.set("orgWide", "true");
  else if (params.workspaceVersionId != null)
    qs.set("workspaceVersionId", String(params.workspaceVersionId));

  if (params.releaseFolderId != null)
    qs.set("release_folder_id", String(params.releaseFolderId));
  if (params.projectId != null) qs.set("projectId", String(params.projectId));
  if (params.workspaceId != null)
    qs.set("workspaceId", String(params.workspaceId));
  if (params.includeSubfolders) qs.set("includeSubfolders", "true");
  if (params.window) qs.set("window", params.window);
  if (params.fromDate) qs.set("fromDate", params.fromDate);
  if (params.toDate) qs.set("toDate", params.toDate);

  qs.set("mode", mode);

  return httpClient<{ data: any[] }>(
    httpMethods.GET,
    `${API_ENDPOINTS.CYCLES.dashboard}/execution-trend?${qs.toString()}`,
  ).then((res) => res.data.data);
};
